import { cn } from '@/lib/utils';

import { UserFallbackIcon } from '@/react/components/icons/UserFallbackIcon';

import { useLiveAvatarUrl } from './avatarEvents';
import { FloatingPopover } from './FloatingPopover';
import { useFloatingMenu } from './useFloatingMenu';

export interface SidebarUser {
  name: string;
  role: string;
  avatarUrl?: string | null;
}

export interface SidebarLogout {
  path: string;
  confirm: string;
  label: string;
}

interface SidebarUserProfileProps {
  user: SidebarUser;
  accountPath: string;
  oauthAppsPath?: string;
  logout: SidebarLogout;
  collapsed: boolean;
}

function csrfToken(): string {
  if (typeof document === 'undefined') return '';
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';
}

function Avatar({ user }: { user: SidebarUser }) {
  const avatarUrl = useLiveAvatarUrl(user.avatarUrl);

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={user.name}
        className="w-12 h-12 rounded-full object-cover flex-shrink-0"
      />
    );
  }
  return (
    <div className="w-12 h-12 rounded-full bg-dinnerplate-white-light flex items-center justify-center flex-shrink-0">
      <UserFallbackIcon className="w-6 h-6 text-grey-light" aria-hidden="true" />
    </div>
  );
}

function RoleBadge({ role, className }: { role: string; className?: string }) {
  return (
    <div
      className={cn(
        'inline-flex items-center justify-center px-1.5 py-[3px] bg-dinnerplate-white-light rounded-md w-fit flex-shrink-0',
        className,
      )}
    >
      <span className="text-[10px] font-bold text-text-secondary capitalize whitespace-nowrap">
        {(role ?? '').replace(/_/g, ' ')}
      </span>
    </div>
  );
}

// User card pinned to the foot of the sidebar with a hover dropdown (Account +
// Log out). Replaces the user_profile_hover Stimulus controller.
export function SidebarUserProfile({ user, accountPath, oauthAppsPath, logout, collapsed }: SidebarUserProfileProps) {
  const menu = useFloatingMenu({ placement: 'right-end' });

  const triggerProps = {
    ref: menu.setReference,
    onMouseEnter: menu.show,
    onMouseLeave: menu.scheduleHide,
  };

  return (
    <div className={cn('mt-auto px-4 py-4', collapsed && 'flex justify-center !px-0')}>
      {collapsed ? (
        <button
          type="button"
          {...triggerProps}
          className="p-3 border-[0.5px] border-disable rounded-lg cursor-pointer transition-colors mx-auto flex-shrink-0"
          aria-label={user.name}
        >
          <Avatar user={user} />
        </button>
      ) : (
        <div
          {...triggerProps}
          className="flex items-center gap-3 p-3 border-[0.5px] border-disable rounded-lg cursor-pointer transition-colors flex-shrink-0"
        >
          <Avatar user={user} />
          <div className="flex flex-col gap-1.5 flex-1 min-w-0">
            <span className="text-sm font-bold leading-tight text-text-primary capitalize truncate whitespace-nowrap">
              {user.name}
            </span>
            <RoleBadge role={user.role} />
          </div>
        </div>
      )}

      <FloatingPopover
        menu={menu}
        className="bg-off-white border-[0.5px] border-disable rounded-lg w-52 p-1"
        style={{ boxShadow: '0px 0px 20px 3px rgba(239, 239, 239, 0.5)' }}
      >
        <div className="flex flex-col gap-1">
          {collapsed && (
            <>
              <div className="flex flex-col gap-2 px-6 py-4 bg-off-white rounded-lg">
                <span className="text-sm font-bold text-text-primary capitalize">{user.name}</span>
                <RoleBadge role={user.role} />
              </div>
              <div className="h-0 w-full relative">
                <div className="absolute inset-0 border-t-[0.5px] border-disable" />
              </div>
            </>
          )}

          <a
            href={accountPath}
            className="flex items-center px-6 py-4 text-sm font-bold text-text-primary bg-off-white rounded-lg border border-transparent hover:border-disable transition-colors"
            data-turbo-frame="_top"
          >
            Account
          </a>

          {oauthAppsPath && (
            <a
              href={oauthAppsPath}
              className="flex items-center px-6 py-4 text-sm font-bold text-text-primary bg-off-white rounded-lg border border-transparent hover:border-disable transition-colors"
              data-turbo-frame="_top"
            >
              OAuth apps
            </a>
          )}

          <form action={logout.path} method="post" className="m-0">
            <input type="hidden" name="authenticity_token" value={csrfToken()} />
            <button
              type="submit"
              data-turbo-confirm={logout.confirm}
              className="w-full flex items-center px-6 py-4 text-sm font-bold text-text-primary bg-off-white rounded-lg border border-transparent hover:border-disable transition-colors text-left"
            >
              {logout.label}
            </button>
          </form>
        </div>
      </FloatingPopover>
    </div>
  );
}
